import type { PhoneRef } from '@core/api/generated';

export interface PersonnelListData {
  id: number;
  org_id: number;
  per_status: PersonnelListStatus;
  user_type_id: number;
  user_type_display: string;
  full_name: string;
  map_address: string;
  statusText: string;
  org_denom: string;
  org_denom_display: string;
  org_name: string;
  org_tax: string;
  roles: string[];
  infrastructures: number;
  equipment: number;
  infrastructures_petitioned: number;
  equipment_petitioned: number;
}
interface PersonnelListStatus {
  id: number;
  stat_name: string;
}

interface PersonnelListAddress {
  id: number;
  adr_addst: string;
  adr_addpc: string;
  adr_addtwn: string;
  adr_addprv: string;
  adr_addterr: string;
  loc_municipality_id: number;
  loc_division_id: number;
  loc_territory_id: number;
  loc_region_id: number;
}

export interface PersonnelListRoleMap {
  [key: string]: PersonnelListRole[];
}
export interface PersonnelListRole {
  role_name: string;
  role_description: string;
}

export interface PersonnelListOrganization {
  id: number;
  org_id: string;
  org_name: string;
  org_denom: string;
  org_tax: string;
  org_type_other: string | null;
  org_call: string | null;
  org_aocn: string;
  org_aocd: string;
  org_spon: string;
  org_spod: string;
  org_uasn: string;
  org_uasd: string;
  org_email: string | null;
  org_mphone: string | null;
  org_indica: string | null;
  org_commercial_name: string | null;
  org_adr_1: number;
  org_adr_2: number;
  org_note: string | null;
  org_type: number;
  org_status: number;
  createdAt: string;
  updatedAt: string;
}

export type PersonnelStatusLabel =
  'draft' | 'requested' | 'submitted' | 'active' | 'rejected' | 'inactive';

type PersonnelStatusValue = {
  draft: 1;
  requested: 2;
  submitted: 3;
  active: 4;
  rejected: 5;
  inactive: 6;
};

export type PersonnelStatus<T extends PersonnelStatusLabel = PersonnelStatusLabel> = {
  label: T;
  value: PersonnelStatusValue[T];
};

// Same canonical statuses used across resources (see @shared/models/enums Status).
export const PERSONNEL_STATUSES: PersonnelStatus[] = [
  { label: 'draft', value: 1 },
  { label: 'requested', value: 2 },
  { label: 'submitted', value: 3 },
  { label: 'active', value: 4 },
  { label: 'rejected', value: 5 },
  { label: 'inactive', value: 6 },
];

// Grouped into the same validated/pending/inactive/rejected buckets used elsewhere for status dots:
export const PERSONNEL_STATUS_COLORS: Record<PersonnelStatusLabel, string> = {
  active: '#00a013',
  rejected: '#dc2626',
  inactive: '#9ca3af',
  draft: '#9ca3af',
  requested: '#f59e0b',
  submitted: '#f59e0b',
};

// Statuses reachable from the profile status dropdown, each backed by a
// dedicated endpoint (submit/approve/reject/deactivate/reactivate).
const PERSONNEL_STATUS_TRANSITIONS: Record<PersonnelStatusLabel, PersonnelStatusLabel[]> = {
  draft: ['submitted', 'rejected', 'inactive'],
  requested: ['submitted', 'rejected', 'inactive'],
  submitted: ['active', 'rejected', 'inactive'],
  active: ['inactive'],
  inactive: ['active'],
  rejected: ['inactive'],
};

export function getReachablePersonnelStatuses(
  current: PersonnelStatusLabel | null | undefined,
  isSuperAdmin: boolean,
): PersonnelStatusLabel[] {
  if (!current || !(current in PERSONNEL_STATUS_TRANSITIONS)) return [];
  const targets = [...PERSONNEL_STATUS_TRANSITIONS[current]];
  if (current === 'draft' && isSuperAdmin) targets.push('active');
  return [current, ...targets];
}

export function mapPersonnelStatusCode(code: string | null | undefined): PersonnelStatusLabel {
  switch (code) {
    case 'DRF':
      return 'draft';
    case 'REQ':
      return 'requested';
    case 'SUB':
      return 'submitted';
    case 'ACT':
      return 'active';
    case 'REJ':
      return 'rejected';
    case 'DEACT':
    case 'INA':
      return 'inactive';
    default:
      return 'draft';
  }
}

export interface PersonnelProfileStatus {
  id: number;
  stat_name: string;
}

export interface PersonnelProfileRole {
  role: string;
  validated: boolean;
}

export const HELD_ROLE_STATUSES: readonly string[] = ['ACT', 'REQ'];

export function isHeldGrant(grant: { status: string }): boolean {
  return HELD_ROLE_STATUSES.includes(grant.status);
}

export interface PersonnelProfileAddress {
  id: number;
  adr_addst: string;
  adr_addpc: string;
  adr_addtwn: string;
  adr_addprv: string;
  adr_addterr: string;
  loc_municipality_id: number | null;
  loc_division_id: number | null;
  loc_territory_id: number | null;
  loc_region_id: number | null;
}

export interface PersonnelProfileData {
  id: number;
  per_id: string;
  per_user: string;
  per_name1: string;
  per_name2: string;
  per_surn1: string;
  per_surn2: string;
  per_card: string;
  per_adm: number;
  org_id: number;
  per_dep: string;
  per_pos: string;
  lang_id: number;
  per_email: string;
  per_mobil: string;
  per_phone: string;
  /** Raw backend `PhoneRef` (`{dial_code, number, type?}`) — kept so the form can resolve the dial code to an id. */
  mobile?: PhoneRef | null;
  phone?: PhoneRef | null;
  per_indica: string;
  per_adr_1: number;
  per_note: string;
  per_status: PersonnelProfileStatus;
  user_type_id: number;
  per_company: string;
  createdAt: string;
  updatedAt: string;
  roles: PersonnelProfileRole[];
  address: PersonnelProfileAddress;
  org_name: string;
}

export interface UserPreference {
  id: number;
  user_id: number;
  preferred_url: string;
  advers: boolean;
}

export interface PersonnelProfileFormData {
  per_name1: string;
  per_name2: string;
  per_surn1: string;
  per_surn2: string;
  per_dep: string;
  per_pos: string;
  per_email: string;
  per_mobil: string;
  per_phone: string;
  per_indica: string;
  per_adm: number;
  roles: PersonnelProfileRole[];
  address: PersonnelProfileAddress;
  per_note: string;
  per_status: string;
  preferences_url: string;
  org_denom: string;
}

export interface Personnel {}
